/**
 * Toss settings (ARCHITECTURE.md §8, §4, §10, §11).
 *
 * Connect the Toss OpenAPI `clientId` / `clientSecret` (stored as an encrypted
 * `client_secret` credential), test it (a successful OAuth token issue unlocks
 * the `toss` gate — the prerequisite for arming trading), then sync the broker
 * account cache (`getAccounts` is rate-limited to 1 req/s, so it is cached in
 * D1). A verified Toss credential is what makes the arm switch usable.
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@richrich/ui";
import { useI18n } from "@richrich/i18n/react";
import type { Account, CredentialSummary, PutCredential } from "@richrich/contracts";
import { PageShell } from "../../components/PageShell";
import { settingsQuery } from "../../lib/query";
import { accountsQuery, credentialsQuery } from "../../features/settings/api";
import { usePutCredential, useSyncAccounts } from "../../features/settings/hooks";
import {
  Button,
  EmptyState,
  ErrorNote,
  SectionCard,
  TextField,
} from "../../components/ui/controls";
import { CredentialActions, CredentialStatus } from "../../features/settings/CredentialControls";

export const Route = createFileRoute("/settings/toss")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(settingsQuery),
      context.queryClient.ensureQueryData(credentialsQuery),
    ]);
  },
  component: TossSettingsPage,
});

function TossSettingsPage() {
  const { t } = useI18n();
  const settings = useQuery(settingsQuery);
  const credentials = useQuery(credentialsQuery);
  const summary = credentials.data?.credentials.find((c) => c.kind === "toss");
  const tossReady = settings.data?.features.toss === true;

  return (
    <PageShell
      eyebrow={t("settings.tossEyebrow")}
      title={t("settings.gateTossTitle")}
      description={t("settings.tossDescription")}
      loading={credentials.isLoading}
    >
      <div className="flex flex-col gap-6">
        <CredentialSection summary={summary} />
        <AccountsSection enabled={tossReady} />
      </div>
    </PageShell>
  );
}

function CredentialSection({ summary }: { summary: CredentialSummary | undefined }) {
  const { t } = useI18n();
  const put = usePutCredential("toss");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  function submit() {
    if (clientId.trim().length === 0 || clientSecret.trim().length === 0) return;
    const body: PutCredential = {
      authType: "client_secret",
      clientId: clientId.trim(),
      clientSecret: clientSecret.trim(),
    };
    put.mutate(body, {
      onSuccess: () => {
        setClientId("");
        setClientSecret("");
      },
    });
  }

  return (
    <SectionCard
      title={t("settings.apiCredentialsTitle")}
      description={t("settings.apiCredentialsDescription")}
      aside={<CredentialStatus summary={summary} />}
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label={t("settings.clientIdLabel")}
            mono
            autoComplete="off"
            placeholder="live-…"
            value={clientId}
            onValueChange={setClientId}
          />
          <TextField
            label={t("settings.clientSecretLabel")}
            type="password"
            mono
            autoComplete="off"
            placeholder="••••••••"
            value={clientSecret}
            onValueChange={setClientSecret}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
          <Button
            variant="primary"
            pending={put.isPending}
            disabled={clientId.trim().length === 0 || clientSecret.trim().length === 0}
            onClick={submit}
          >
            {summary !== undefined
              ? t("settings.replaceCredentials")
              : t("settings.saveCredentials")}
          </Button>
          <CredentialActions kind="toss" summary={summary} />
          {put.isError ? <ErrorNote>{t("settings.saveCredentialsFailed")}</ErrorNote> : null}
        </div>
      </div>
    </SectionCard>
  );
}

function AccountsSection({ enabled }: { enabled: boolean }) {
  const { t } = useI18n();
  const accounts = useQuery({ ...accountsQuery, enabled });
  const sync = useSyncAccounts();

  if (!enabled) {
    return (
      <SectionCard
        title={t("settings.accountsTitle")}
        description={t("settings.accountsLockedDescription")}
      >
        <EmptyState
          title={t("settings.verifyTossToSync")}
          description={t("settings.verifyTossToSyncDescription")}
        />
      </SectionCard>
    );
  }

  const rows = accounts.data?.accounts ?? [];

  return (
    <SectionCard
      title={t("settings.accountsTitle")}
      description={t("settings.accountsDescription")}
      aside={
        <Button
          variant="secondary"
          size="sm"
          pending={sync.isPending}
          onClick={() => sync.mutate()}
        >
          {t("settings.syncNow")}
        </Button>
      }
    >
      {sync.isError ? <ErrorNote>{t("settings.syncFailed")}</ErrorNote> : null}
      {rows.length === 0 ? (
        <EmptyState
          title={t("settings.noAccounts")}
          description={t("settings.noAccountsDescription")}
          action={
            <Button
              variant="primary"
              size="sm"
              pending={sync.isPending}
              onClick={() => sync.mutate()}
            >
              {t("settings.syncAccounts")}
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((account) => (
            <AccountRow key={account.accountSeq} account={account} />
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function AccountRow({ account }: { account: Account }) {
  const { t } = useI18n();
  return (
    <li className="flex items-center justify-between gap-3 rounded-md border border-hairline bg-page px-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-ink">
            {account.name ?? account.number ?? account.accountSeq}
          </span>
          {account.isPrimary ? (
            <Badge tone="info" dot>
              {t("settings.primaryBadge")}
            </Badge>
          ) : null}
        </div>
        <div className="mt-0.5 font-mono text-xs text-muted">
          {account.number ?? account.accountSeq}
          {account.accountType !== null ? ` · ${account.accountType}` : ""}
        </div>
      </div>
      {account.currency !== null ? (
        <span className="font-mono text-xs text-ink-secondary">{account.currency}</span>
      ) : null}
    </li>
  );
}
